import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, Paper, List, ListItem, Divider, IconButton, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Switch, Grid, CircularProgress, Alert } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const LookerEmbedList = () => {
  const [embeds, setEmbeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentEmbed, setCurrentEmbed] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    embedUrl: '',
    isPublic: true,
    height: '600px',
    width: '100%'
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [embedToDelete, setEmbedToDelete] = useState(null);
  const { clientId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmbeds();
  }, [clientId]);

  const fetchEmbeds = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`/api/clients/${clientId}/looker-embeds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        setEmbeds(response.data.data.embeds);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Looker Studio embeds:', error);
      setError('Erro ao carregar dashboards. Por favor, tente novamente.');
      setLoading(false);
      
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    }
  };

  const handleOpenDialog = (embed = null) => {
    if (embed) {
      setCurrentEmbed(embed);
      setFormData({
        name: embed.name,
        description: embed.description || '',
        embedUrl: embed.embedUrl,
        isPublic: embed.isPublic,
        height: embed.height || '600px',
        width: embed.width || '100%'
      });
    } else {
      setCurrentEmbed(null);
      setFormData({
        name: '',
        description: '',
        embedUrl: '',
        isPublic: true,
        height: '600px',
        width: '100%'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'isPublic' ? checked : value
    });
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (currentEmbed) {
        // Atualizar embed existente
        await axios.patch(`/api/clients/${clientId}/looker-embeds/${currentEmbed._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Criar novo embed
        await axios.post(`/api/clients/${clientId}/looker-embeds`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      handleCloseDialog();
      fetchEmbeds();
    } catch (error) {
      console.error('Error saving Looker Studio embed:', error);
      setError('Erro ao salvar dashboard. Por favor, tente novamente.');
    }
  };

  const handleDeleteClick = (embed) => {
    setEmbedToDelete(embed);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`/api/clients/${clientId}/looker-embeds/${embedToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeleteConfirmOpen(false);
      fetchEmbeds();
    } catch (error) {
      console.error('Error deleting Looker Studio embed:', error);
      setError('Erro ao excluir dashboard. Por favor, tente novamente.');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(embeds);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setEmbeds(items);
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.patch(`/api/clients/${clientId}/looker-embeds/reorder`, {
        embedIds: items.map(item => item._id)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error reordering Looker Studio embeds:', error);
      setError('Erro ao reordenar dashboards. Por favor, tente novamente.');
      // Reverter mudanças em caso de erro
      fetchEmbeds();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Dashboards Looker Studio</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo Dashboard
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : embeds.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Nenhum dashboard configurado
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Adicione seu primeiro dashboard do Looker Studio para visualizar dados avançados.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Adicionar Dashboard
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%' }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <List {...provided.droppableProps} ref={provided.innerRef}>
                  {embeds.map((embed, index) => (
                    <Draggable key={embed._id} draggableId={embed._id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <ListItem
                            secondaryAction={
                              <Box>
                                <IconButton edge="end" onClick={() => handleOpenDialog(embed)}>
                                  <EditIcon />
                                </IconButton>
                                <IconButton edge="end" onClick={() => handleDeleteClick(embed)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            }
                          >
                            <Box {...provided.dragHandleProps} sx={{ mr: 2 }}>
                              <DragIcon />
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1">{embed.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {embed.description || 'Sem descrição'}
                              </Typography>
                            </Box>
                          </ListItem>
                          {index < embeds.length - 1 && <Divider />}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </Paper>
      )}
      
      {/* Dialog para criar/editar dashboards */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{currentEmbed ? 'Editar Dashboard' : 'Novo Dashboard'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {currentEmbed 
              ? 'Atualize as informações do dashboard abaixo.' 
              : 'Para adicionar um dashboard do Looker Studio, cole o link de incorporação disponível nas configurações de compartilhamento do relatório.'}
          </DialogContentText>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="name"
                label="Nome do Dashboard"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="description"
                label="Descrição"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="embedUrl"
                label="URL de Incorporação (Embed)"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.embedUrl}
                onChange={handleInputChange}
                required
                helperText="Cole o URL de incorporação disponível nas configurações de compartilhamento do Looker Studio."
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="height"
                label="Altura"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.height}
                onChange={handleInputChange}
                helperText="Ex: 600px, 80vh"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="width"
                label="Largura"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.width}
                onChange={handleInputChange}
                helperText="Ex: 100%, 800px"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublic}
                    onChange={handleInputChange}
                    name="isPublic"
                  />
                }
                label="Dashboard público (não requer autenticação)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {currentEmbed ? 'Atualizar' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog para confirmar exclusão */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o dashboard "{embedToDelete?.name}"?
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LookerEmbedList;
